import type {
  Allergen,
  PricingPlan,
  SubscriptionItemType,
  SubscriptionType,
} from "@/primary-models";
import type { EProductCategory } from "@/constants/products";
import type {
  ISubscriptionOrderCharges,
  ISubscriptionOrderChargeDiscount,
  ISubscriptionOrderChargeProduct,
  ISubscriptionOrderItem,
  ISubscriptionOverview,
  ISubscriptionOrders,
  TFreshMealPlan,
  TLitterPlan,
} from "@api-clients/subscriptions";
import { Decimal, toCurrencyDecimalsString } from "@/decimal-util";
import dayjs, { Dayjs } from "dayjs";
import {
  areDatesOnSameDay,
  areSomeDatesValid,
  formatDateStringAsValue,
  getNow,
  sortDateStrings,
} from "../../misc/src/dates";
import {
  TProductQuantities,
  TSubscriptionOverviewContent,
  TTotalBagsPerCadence,
  TTotalMeals,
  TTypedDate,
} from "./subscriptionOverview.types";
import { MEALS_PER_TRAY } from "./subscriptionOverview.constants";

export const hasSubscriptionsOverviewGotFreshKitten = (subscriptions: ISubscriptionOverview) => {
  return subscriptions.freshMealPlans.some((subscription) => subscription.cat.lifeStage === "KITTEN");
};

export const hasSubscriptionsOverviewGotFreshKittensOnly = (subscriptions: ISubscriptionOverview) => {
  return !subscriptions.freshMealPlans.some((subscription) => subscription.cat.lifeStage !== "KITTEN");
};

export const hasSubscriptionsOverviewGotFreshOnly = (subscriptions: ISubscriptionOverview) => {
  return subscriptions.freshMealPlans.length > 0 && subscriptions.litterPlans.length === 0;
};

export const hasSubscriptionsOverviewGotScoopOnly = (subscriptions: ISubscriptionOverview) => {
  return subscriptions.freshMealPlans.length === 0 && subscriptions.litterPlans.length > 0;
};

export const getSubscriptionOverviewContent = (
  subscriptions: ISubscriptionOverview,
): TSubscriptionOverviewContent | null => {
  const hasFresh = subscriptions.freshMealPlans.length > 0;
  const hasLitter = subscriptions.litterPlans.length > 0;

  if (hasFresh && hasLitter) {
    return "FRESH_AND_LITTER";
  }

  if (hasFresh) {
    return "FRESH";
  }

  if (hasLitter) {
    return "LITTER";
  }

  return null;
};

export const getTotalPriceFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges[],
): number => charges.reduce((sum, charge) => sum + Number(charge.total), 0);

export const getTotalDiscountFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges[],
): number => charges
  .flatMap((charge) => charge.discounts)
  .reduce((sum, discount) => sum + Number(discount.saving), 0);

export const getDeliveryFeeFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges[],
): number => charges.reduce((sum, charge) => sum + Number(charge.delivery.total), 0);

export const getDiscountsFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges[],
) => charges.flatMap((charge) => charge.discounts);

export const getMergedDiscountsFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges[],
): ISubscriptionOrderChargeDiscount[] => Array.from(
  getDiscountsFromSubscriptionOrderCharges(charges).reduce((discounts, discount) => {
    const existingDiscount = discounts.get(discount.code);

    discounts.set(discount.code, existingDiscount
      ? {
        ...existingDiscount,
        saving: toCurrencyDecimalsString(
          new Decimal(existingDiscount.saving).plus(discount.saving),
        ),
      }
      : discount);

    return discounts;
  }, new Map<string, ISubscriptionOrderChargeDiscount>()).values(),
);

const getProductsFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges | null | undefined,
): ISubscriptionOrderChargeProduct[] => charges?.products ?? [];

export const getFreshChargeFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges | null | undefined,
): ISubscriptionOrderChargeProduct | undefined => (
  getProductsFromSubscriptionOrderCharges(charges).find(
    (charge) => charge.productType === "FRESH",
  )
);

export const getExtrasTotalFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges | null | undefined,
): number => getProductsFromSubscriptionOrderCharges(charges)
  .filter((charge) => charge.productType !== "FRESH")
  .reduce((total, charge) => total + Number(charge.total), 0);

export const getProductChargeFromSubscriptionOrderCharges = (
  charges: ISubscriptionOrderCharges | null | undefined,
  productKey: ISubscriptionOrderChargeProduct["productKey"],
): ISubscriptionOrderChargeProduct | undefined => (
  getProductsFromSubscriptionOrderCharges(charges).find(
    (charge) => charge.productKey === productKey,
  )
);

export const hasSubscriptionsOverviewGotInactiveFresh = (subscriptions: ISubscriptionOverview) => {
  return subscriptions.freshMealPlans.some((subscription) => subscription.status === "CANCELLED");
};

export const getCancelledFreshMealPlans = (
  subscriptions: ISubscriptionOverview,
): TFreshMealPlan[] => {
  return subscriptions.freshMealPlans.filter((plan) => plan.status === "CANCELLED");
};

export const getCancelledPlansFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
  productTypes: SubscriptionType[],
): Array<TFreshMealPlan | TLitterPlan> => {
  return [
    ...subscriptions.freshMealPlans,
    ...subscriptions.litterPlans,
  ].filter((plan) => (
    plan.status === "CANCELLED" && productTypes.includes(plan.type)
  ));
};

export const doesFreshSubscriptionMatchFulfilledBoxCount = (
  subscription: TFreshMealPlan,
  numberFulfilled: number,
) => {
  return subscription.fulfillment.boxesFulfilled === numberFulfilled;
};

export const isFreshSubscriptionOfPlanType = (
  subscription: TFreshMealPlan,
  planType: PricingPlan,
) => {
  return subscription.pricingPlan === planType;
};

export const hasSubscriptionsOverviewGotActiveFreshMulticat = (subscriptions: ISubscriptionOverview) => {
  return subscriptions.freshMealPlans.filter((subscription) => subscription.status === "ACTIVE").length > 1;
};

export const getMostFulfilledFreshSubscriptionFromOverview = (
  subscriptions: ISubscriptionOverview,
): TFreshMealPlan | undefined => {
  return [ ...subscriptions.freshMealPlans ]
    .sort((subscriptionA, subscriptionB) => (
      subscriptionB.fulfillment.boxesFulfilled -
      subscriptionA.fulfillment.boxesFulfilled
    ))[0];
};

export const getAllFreshMealPlansFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
): TFreshMealPlan[] => {
  return subscriptions.freshMealPlans;
};

export const getActiveFreshMealPlansFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
): TFreshMealPlan[] => {
  return subscriptions.freshMealPlans.filter((plan) => plan.status === "ACTIVE");
};

export const getAllLitterPlansFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
): TLitterPlan[] => {
  return subscriptions.litterPlans;
};

export const getAllSubscriptionOrderChargesFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
  order: keyof ISubscriptionOrders,
): ISubscriptionOrderCharges[] => (
  [
    ...getAllFreshMealPlansFromSubscriptionOverview(subscriptions),
    ...getAllLitterPlansFromSubscriptionOverview(subscriptions),
  ].flatMap((plan) => {
    const charges = plan.orders?.[order]?.charges;

    return charges ? [ charges ] : [];
  })
);

export const getPlanetLitterFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
): TLitterPlan | null => {
  return getAllLitterPlansFromSubscriptionOverview(subscriptions).find((plan) => plan.type === "SCOOP_PLANET") || null;
};

export const getActiveLitterPlansFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
): TLitterPlan[] => {
  return subscriptions.litterPlans.filter((plan) => plan.status === "ACTIVE");
};

export const getActivePlansFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
): ISubscriptionOverview => {
  return {
    freshMealPlans: getActiveFreshMealPlansFromSubscriptionOverview(subscriptions),
    litterPlans: getActiveLitterPlansFromSubscriptionOverview(subscriptions),
    hasFreeSprinklesForLife: subscriptions.hasFreeSprinklesForLife,
  };
};

export const hasSubscriptionsOverviewGotActivePlans = (
  subscriptions: ISubscriptionOverview,
): boolean => {
  const activePlans = getActivePlansFromSubscriptionOverview(subscriptions);

  return activePlans.freshMealPlans.length > 0 || activePlans.litterPlans.length > 0;
};

export const hasSubscriptionsOverviewGotCancelledPlansOnly = (
  subscriptions: ISubscriptionOverview,
): boolean => {
  const hasPlans = subscriptions.freshMealPlans.length > 0 || subscriptions.litterPlans.length > 0;

  return hasPlans && !hasSubscriptionsOverviewGotActivePlans(subscriptions);
};


export const getActiveFreshCatNamesFromSubscriptionOverview = (
  subscriptions: ISubscriptionOverview,
): string[] => {
  const plans = getActiveFreshMealPlansFromSubscriptionOverview(subscriptions);
  return plans.map((plan) => plan.cat.name);
};


export const getAllergiesFromActiveFreshSubscriptions = (
  subscriptions: ISubscriptionOverview,
): Allergen[] => {
  const plans = getActiveFreshMealPlansFromSubscriptionOverview(subscriptions);
  return plans.flatMap((plan) => plan.cat.allergies);
};

export const getRecipesFromActiveFreshSubscriptions = (
  subscriptions: ISubscriptionOverview,
): SubscriptionItemType[] => {
  const plans = getActiveFreshMealPlansFromSubscriptionOverview(subscriptions);
  return plans.flatMap((plan) => (
    plan.items.filter((
      item,
    ) => item.type === "FRESH").map((
      item,
    ) => item.name)
  ));
};


export const doesSubscriptionContainCatWithAllergy = (
  subscriptions: ISubscriptionOverview,
  allergies: Allergen | Allergen[],
) => {
  const subscriptionAllergies = getAllergiesFromActiveFreshSubscriptions(subscriptions);
  if (Array.isArray(allergies)) {
    return subscriptionAllergies.some((subscriptionAllergy) => allergies.includes(subscriptionAllergy));
  }
  return subscriptionAllergies.includes(allergies);
};

export const doesSubscriptionContainCatWithRecipe = (
  subscriptions: ISubscriptionOverview,
  recipes: SubscriptionItemType | SubscriptionItemType[],
) => {
  const subscriptionRecipes = getRecipesFromActiveFreshSubscriptions(subscriptions);
  if (Array.isArray(recipes)) {
    return subscriptionRecipes.some((subscriptionRecipe) => recipes.includes(subscriptionRecipe));
  }
  return subscriptionRecipes.includes(recipes);
};

export const hasBeenAtLeastDaysSinceLastFreshSubscriptionDelivery = (
  subscription: TFreshMealPlan,
  numberOfDays: number,
) => {
  const { lastDeliveryDate } = subscription.date;

  return lastDeliveryDate !== null
    && !dayjs(lastDeliveryDate).add(numberOfDays, "days").isAfter(getNow());
};

export const getAllInProgressDeliveryDatesFromOverview = (subscriptions: ISubscriptionOverview) => {
  const inProgressFreshDeliveryDates = subscriptions.freshMealPlans.map((plan) => plan.date.inProgressDelivery);
  const inProgressLitterDeliveryDates = subscriptions.litterPlans.map((plan) => plan.date.inProgressDelivery);

  return [
    ...inProgressFreshDeliveryDates,
    ...inProgressLitterDeliveryDates,
  ];
};

export const getAllNextDeliveryDatesFromOverview = (subscriptions: ISubscriptionOverview) => {
  const nextFreshDeliveryDates = subscriptions.freshMealPlans.map((plan) => plan.date.nextDelivery);
  const nextLitterDeliveryDates = subscriptions.litterPlans.map((plan) => plan.date.nextDelivery);

  return [
    ...nextFreshDeliveryDates,
    ...nextLitterDeliveryDates,
  ];
};

export const doAllSubscriptionsHaveSameInProgressDeliveryDate = (
  subscriptions: ISubscriptionOverview,
): TTypedDate | false => {
  const inProgressDeliveryDates = getAllInProgressDeliveryDatesFromOverview(subscriptions);
  if (areDatesOnSameDay(inProgressDeliveryDates)) {
    return {
      type: "inProgress",
      date: dayjs(inProgressDeliveryDates[0]),
    };
  }
  return false;
};

export const doAllSubscriptionsHaveSameNextDeliveryDate = (
  subscriptions: ISubscriptionOverview,
): TTypedDate | false => {
  const nextDeliveryDates = getAllNextDeliveryDatesFromOverview(subscriptions);
  if (areDatesOnSameDay(nextDeliveryDates)) {
    return {
      type: "next",
      date: dayjs(nextDeliveryDates[0]),
    };
  }
  return false;
};

export const doAllSubscriptionsHaveSameTrueNextDeliveryDate = (
  subscriptions: ISubscriptionOverview,
): TTypedDate | false => {
  const inProgressDeliveryDates = getAllInProgressDeliveryDatesFromOverview(subscriptions);
  const inProgressDeliveryDate = doAllSubscriptionsHaveSameInProgressDeliveryDate(subscriptions);
  const nextDeliveryDate = doAllSubscriptionsHaveSameNextDeliveryDate(subscriptions);

  if (inProgressDeliveryDate) {
    return inProgressDeliveryDate;
  }

  if (areSomeDatesValid(inProgressDeliveryDates)) {
    return false;
  }

  if (nextDeliveryDate) {
    return nextDeliveryDate;
  }

  return false;

};

export const getNumberOfMealsFromNumberOfTrays = (numberOfTrays: number) => (
  numberOfTrays * MEALS_PER_TRAY
);



export const getTotalFreshMealsFromSubscription = (subscriptions: ISubscriptionOverview): TTotalMeals => {
  const freshMeals = getAllFreshMealPlansFromSubscriptionOverview(subscriptions);

  const numberOfTrays = freshMeals.reduce((total, plan) => total + plan.planSize, 0);

  return {
    numberOfTrays,
    numberOfMeals: getNumberOfMealsFromNumberOfTrays(numberOfTrays),
  };
};

export const getTotalBagsPerCadenceFromPlanetLitterSubscription = (
  subscriptions: ISubscriptionOverview,
): TTotalBagsPerCadence | null => {
  const plan = getPlanetLitterFromSubscriptionOverview(subscriptions);

  if (!plan) { return null; }

  return {
    numberOfBags: plan.items[0].quantity,
    cadence: plan.frequency,
  };
};

export const areAllFreshSubscriptionsOnTheSamePlan = (subscriptions: ISubscriptionOverview) => {
  const freshMealSubscriptions = getAllFreshMealPlansFromSubscriptionOverview(subscriptions);
  const plans = freshMealSubscriptions.map((plan) => plan.pricingPlan);

  if (!freshMealSubscriptions.length) { return false; }

  return plans.every((plan) =>plan === plans[0]);
};

export const getAllDeliveryDatesFromSubscriptionOverview = (subscriptions: ISubscriptionOverview): Dayjs[] => {
  const rawDates = [
    ...getAllInProgressDeliveryDatesFromOverview(subscriptions),
    ...getAllNextDeliveryDatesFromOverview(subscriptions),
  ];

  const dates: string[] = [];

  rawDates.forEach((rawDate) => {
    if (!rawDate) { return; }
    const formattedDate = formatDateStringAsValue(rawDate);
    if (dates.includes(formattedDate)) { return; }
    dates.push(formattedDate);
  });

  return sortDateStrings(dates);
};

export const isInProgressDeliveryDate = (
  date: string,
  subscriptions: ISubscriptionOverview,
): boolean => {
  const plans = [
    ...subscriptions.freshMealPlans,
    ...subscriptions.litterPlans,
  ];

  return plans.some((plan) => (
    plan.date.inProgressDelivery !== null
    && formatDateStringAsValue(plan.date.inProgressDelivery) === date
  ));
};

/**
 * @remarks
 * A delivery may intentionally appear in two buckets when it has both
 * an in-progress date and a next delivery date.
 */
const getSubscriptionsByDeliveryDate = (
  subscriptions: ISubscriptionOverview,
  dateString: string,
): ISubscriptionOverview => {
  const fresh = getAllFreshMealPlansFromSubscriptionOverview(subscriptions);
  const litter = getAllLitterPlansFromSubscriptionOverview(subscriptions);

  return {
    ...subscriptions,
    freshMealPlans: fresh.filter((plan) => [
      formatDateStringAsValue(plan.date.inProgressDelivery),
      formatDateStringAsValue(plan.date.nextDelivery),
    ].includes(formatDateStringAsValue(dateString))),
    litterPlans: litter.filter((plan) => [
      formatDateStringAsValue(plan.date.inProgressDelivery),
      formatDateStringAsValue(plan.date.nextDelivery),
    ].includes(formatDateStringAsValue(dateString))),
  };
};

export const getDateToSubscriptionOverviewMap = (
  subscriptions: ISubscriptionOverview,
): Record<string, ISubscriptionOverview> => {
  const dates = getAllDeliveryDatesFromSubscriptionOverview(subscriptions);

  const dateToSubscriptionMap: Record<string, ISubscriptionOverview> = {};

  dates.forEach((date) => {
    dateToSubscriptionMap[formatDateStringAsValue(date.toString())] =
      getSubscriptionsByDeliveryDate(subscriptions, date.toString());
  });

  return dateToSubscriptionMap;
};

const getProductsByCategoryFromItems = (
  items: ISubscriptionOrderItem[],
  category: `${EProductCategory}`,
): TProductQuantities[] => {
  return Array.from(
    items
      .filter((item) => item.type === category)
      .reduce((productsByName, { name, quantity }) => {
        productsByName.set(
          name,
          (productsByName.get(name) ?? 0) + quantity,
        );

        return productsByName;
      }, new Map<SubscriptionItemType, number>()),
    ([ title, quantity ]) => ({ title, quantity }),
  );
};

const getProductsByCategoryFromPlans = (
  plans: Array<TFreshMealPlan | TLitterPlan>,
  category: `${EProductCategory}`,
): TProductQuantities[] => (
  getProductsByCategoryFromItems(
    plans.flatMap((plan) => plan.items),
    category,
  )
);

export const getAllFreshProductsFromSubscriptionOverview = (subscriptions: ISubscriptionOverview) => (
  getProductsByCategoryFromPlans(
    getActiveFreshMealPlansFromSubscriptionOverview(subscriptions),
    "FRESH",
  )
);

const getAllProductsFromFreshMealPlanOrders = (
  plan: TFreshMealPlan,
  order: keyof ISubscriptionOrders,
  category: `${EProductCategory}`,
) => getProductsByCategoryFromItems(plan.orders?.[order]?.items ?? [], category);

export const isOrderInProgress = (
  order: keyof ISubscriptionOrders,
): order is "inProgressOrder" => order === "inProgressOrder";

export const getAllFreshProductsFromFreshMealPlan = (plan: TFreshMealPlan) => (
  getProductsByCategoryFromPlans([ plan ], "FRESH")
);

export const getAllFreshProductsFromFreshMealPlanOrders = (
  plan: TFreshMealPlan,
  order: keyof ISubscriptionOrders,
) => getAllProductsFromFreshMealPlanOrders(plan, order, "FRESH");

export const getFreshMealPlanOrderTotal = (
  plan: TFreshMealPlan,
  order: keyof ISubscriptionOrders,
): number => Number(
  plan.orders?.[order]?.charges?.products.find(
    (charge) => charge.productType === "FRESH",
  )?.total,
);

export const getFreshMealPlanOrderSubtotal = (
  plan: TFreshMealPlan,
  order: keyof ISubscriptionOrders,
): number => Number(
  plan.orders?.[order]?.charges?.products.find(
    (charge) => charge.productType === "FRESH",
  )?.subtotal,
);

export const getTotalFreshMealsFromFreshMealPlanOrder = (
  plan: TFreshMealPlan,
  order: keyof ISubscriptionOrders,
): number => getAllFreshProductsFromFreshMealPlanOrders(plan, order)
  .reduce((total, item) => total + item.quantity, 0);

export const getAllExtraProductsFromFreshMealPlan = (plan: TFreshMealPlan) => (
  getProductsByCategoryFromPlans([ plan ], "EXTRAS")
);

export const getAllExtraProductsFromFreshMealPlanOrders = (
  plan: TFreshMealPlan,
  order: keyof ISubscriptionOrders,
) => getAllProductsFromFreshMealPlanOrders(plan, order, "EXTRAS");

const getAllProductsFromSubscriptionOrders = (
  subscriptions: ISubscriptionOverview,
  order: keyof ISubscriptionOrders,
  category: `${EProductCategory}`,
) => (
  getProductsByCategoryFromItems(
    getAllFreshMealPlansFromSubscriptionOverview(subscriptions)
      .flatMap((plan) => plan.orders?.[order]?.items ?? []),
    category,
  )
);

export const getAllFreshProductsFromSubscriptionOrders = (
  subscriptions: ISubscriptionOverview,
  order: keyof ISubscriptionOrders,
) => getAllProductsFromSubscriptionOrders(subscriptions, order, "FRESH");

export const getAllExtraProductsFromSubscriptionOverview = (subscriptions: ISubscriptionOverview) => (
  getProductsByCategoryFromPlans(
    getActiveFreshMealPlansFromSubscriptionOverview(subscriptions),
    "EXTRAS",
  )
);

export const getAllExtraProductsFromSubscriptionOrders = (
  subscriptions: ISubscriptionOverview,
  order: keyof ISubscriptionOrders,
) => getAllProductsFromSubscriptionOrders(subscriptions, order, "EXTRAS");

export const getExtrasCount = (
  extras: readonly { quantity: number }[] | null | undefined,
): number => extras?.reduce((total, extra) => total + extra.quantity, 0) ?? 0;

export const getAllLitterProductsFromSubscriptionOverview = (subscriptions: ISubscriptionOverview) => (
  getProductsByCategoryFromPlans(
    getActiveLitterPlansFromSubscriptionOverview(subscriptions),
    "SCOOP",
  )
);

export const getAllLitterProductsFromLitterPlanOrders = (
  plan: TLitterPlan,
  order: keyof ISubscriptionOrders,
) => getProductsByCategoryFromItems(plan.orders?.[order]?.items ?? [], "SCOOP");
